import { service } from '@ember/service'
import Component from '@glimmer/component'
import type { IntlService } from 'ember-intl'
import type { LegendOptions, Point, SeriesOptionsType } from 'highcharts'
import { SummaryBox, type Stat } from 'teacher-ui-client/components/giraffe/summary-box'
import { colors } from '@colors'
import { LeftSplitChart } from 'teacher-ui-client/components/giraffe/left-split-chart'
import { ScatterPlot } from 'teacher-ui-client/components/giraffe/primitives/scatter-plot'
import { SummaryBoxColorScheme } from 'teacher-ui-client/components/giraffe/summary-box/themes'
import { formatValue } from 'teacher-ui-client/utils/format-value'

export interface BooksReadStudentData {
  summary: {
    total_count: number
    average_lexile_level: Nullable<number>
    average_lexile_formatted: Nullable<string>
  }
  events: {
    category: 'library' | 'my_program' | 'CompleteBookChapterQuiz' | 'CompleteBook'
    book_name: string
    created_at: number
    lexile: number
    lexile_formatted: string
  }[]
}

interface Args {
  data: BooksReadStudentData
  period: {
    start: Date
    end: Date
  }
  product: 're' | 'rex'
  summaryTitle: string
}

interface Signature {
  Element: HTMLDivElement
  Args: Args
}

function getColor(product: string, category: string): string {
  switch (`${product}.${category}`) {
    case 're.library':
      return colors.purplyPink300
    case 're.my_program':
      return colors.juicyOrange300
    case 'rex.CompleteBook':
      return colors.juicyOrange300
    case 'rex.CompleteBookChapterQuiz':
      return colors.purplyPink300
    default:
      return colors.oceanyBlue300
  }
}

export class BooksReadStudent extends Component<Signature> {
  @service intl!: IntlService

  /**
   * Chart legend. Puts the legend top left of chart
   */
  readonly legend: LegendOptions = {
    enabled: true,
    align: 'left',
    verticalAlign: 'top',
    x: 55,
  }

  readonly yAxisLabel: string = 'Lexile'

  /**
   * Returns a formatter function to pass to Highcharts to format the tooltip label.
   */
  toolTipFormatter(this: Point): string {
    const { event } = this as Point & { event: { book_name: string } }
    return `Book: ${event.book_name}</br> Lexile: ${this.y}L`
  }

  /**
   * Defines categories per product
   * The categories known so far are:
   *  'CompleteBookChapterQuiz', 'CompleteBook' for REX
   *  'library', 'my_program' for RE
   */
  get categories(): string[] {
    if (this.args.product === 're') return ['library', 'my_program']
    // intentionally 'CompleteBook' event for the Book Quizzes legend
    return ['CompleteBookChapterQuiz', 'CompleteBook']
  }

  /**
   * All the book events formatted for highcharts into series of data.
   */
  get chartData(): SeriesOptionsType[] {
    const { intl, eventsPerCategory } = this
    const { product } = this.args

    const chartData = eventsPerCategory.map((categoryEvents) => {
      const { category } = categoryEvents
      const legendTitle = intl.t(`reporting.charts.booksReadStudent.${category}`)

      const color = getColor(product, category)

      return {
        name: legendTitle,
        data: categoryEvents.events,
        color,
        type: 'scatter' as const,
      }
    })

    return chartData
  }

  /**
   * Completed events for all categories formatted per category for highcharts
   */
  get eventsPerCategory() {
    const { categories } = this
    const bookEvents = this.args.data.events

    const eventData = categories.map((category) => {
      const bookEventsPerCategory = bookEvents.filter((event) => event.category === category)
      const createdAtAndLexileInfo = bookEventsPerCategory.map((event) => {
        const timestampInMilliseconds = event.created_at * 1000
        return { x: timestampInMilliseconds, y: event.lexile, event }
      })

      return {
        category,
        events: createdAtAndLexileInfo,
      }
    })

    return eventData
  }

  /**
   * List of summary statistics, extracted from the input data.
   * Display total books read and average lexile level for the student.
   */
  get summaryStats(): Stat[] {
    const bookLabel = this.intl.t('reporting.summaryTitles.booksReadStudent')
    const lexileLabel = this.intl.t('reporting.averageLexileLevel')

    const totalRead = this.args.data.summary.total_count
    const averageLexileLevel = formatValue(this.args.data.summary.average_lexile_level)

    return [
      { label: bookLabel, value: String(totalRead) },
      { label: lexileLabel, value: String(averageLexileLevel) },
    ]
  }

  <template>
    <div data-test-giraffe-books-read-student-details class="h-full" ...attributes>
      <LeftSplitChart>
        <:left>
          <SummaryBox
            class="h-full"
            @title={{@summaryTitle}}
            @stats={{this.summaryStats}}
            @colorScheme={{SummaryBoxColorScheme.Pink}}
          />
        </:left>
        <:right>
          <ScatterPlot
            @data={{this.chartData}}
            @fromDate={{@period.start}}
            @legend={{this.legend}}
            @toDate={{@period.end}}
            @toolTipFormatter={{this.toolTipFormatter}}
            @yAxisLabel={{this.yAxisLabel}}
          />
        </:right>
      </LeftSplitChart>
    </div>
  </template>
}

export default BooksReadStudent
